import React, { useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { IconButton, Tooltip } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

import { Button } from 'components/Button';
import { useStore } from 'utils/hooks/store';
import { DocumentAccessLevel } from 'utils/api/modules/entities/entities.types';

import { RiskAssessmentTable } from './components/risk-assessment-table';
import { RiskAssessmentConfigDialog } from './components/risk-assessment-config-dialog';
import type {
  CellValue,
  RiskAssessmentConfigData,
  RiskAssessmentNodeComponentProps,
  Row,
} from './types';
import {
  StyledFooter,
  StyledFooterActions,
  StyledRiskAssessmentBody,
  StyledRiskAssessmentContainer,
} from './styles';
import { generateRowId, reconcileRows } from './utils';

export const RiskAssessmentNodeComponent: React.FC<RiskAssessmentNodeComponentProps> = ({
  node: {
    attrs: {
      columns: columnsAttr,
      rows: rowsAttr,
      likelihoodLabels: likelihoodLabelsAttr,
      severityLabels: severityLabelsAttr,
      editable,
    },
  },
  selected,
  editor,
  updateAttributes,
}) => {
  const columns = Array.isArray(columnsAttr) ? columnsAttr : [];
  const rows = Array.isArray(rowsAttr) ? rowsAttr : [];
  const likelihoodLabels = Array.isArray(likelihoodLabelsAttr) ? likelihoodLabelsAttr : [];
  const severityLabels = Array.isArray(severityLabelsAttr) ? severityLabelsAttr : [];

  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [configData, setConfigData] = useState<RiskAssessmentConfigData>({
    columns: [...columns],
    likelihoodLabels: [...likelihoodLabels],
    severityLabels: [...severityLabels],
  });

  const { documentsDetails } = useStore();
  // viewOnlyEditor stays editable:true (TipTap Pro extensions blank the doc otherwise),
  // so we also gate on the document access level here.
  const isEditable = editable && editor.isEditable && documentsDetails.accessLevel !== DocumentAccessLevel.View;

  const handleAddRow = (): void => {
    const newRow: Row = {
      id: generateRowId(),
      values: columns.reduce<Record<string, CellValue>>((acc, col) => {
        acc[col.id] = null;
        return acc;
      }, {}),
    };
    updateAttributes({ rows: [...rows, newRow] });
  };

  const handleRowChange = (rowId: string, columnId: string, value: CellValue): void => {
    const newRows = rows.map((row) =>
      row.id === rowId ? { ...row, values: { ...row.values, [columnId]: value } } : row
    );
    updateAttributes({ rows: newRows });
  };

  const handleRowDelete = (rowId: string): void => {
    updateAttributes({ rows: rows.filter((row) => row.id !== rowId) });
  };

  const handleColumnResize = (columnId: string, width: number): void => {
    const newColumns = columns.map((col) => (col.id === columnId ? { ...col, width } : col));
    updateAttributes({ columns: newColumns });
  };

  const handleConfigOpen = (): void => {
    setConfigData({
      columns: [...columns],
      likelihoodLabels: [...likelihoodLabels],
      severityLabels: [...severityLabels],
    });
    setIsConfigOpen(true);
  };

  const handleConfigSave = (): void => {
    const reconciledRows = reconcileRows(
      rows,
      configData.columns,
      configData.likelihoodLabels.length,
      configData.severityLabels.length
    );

    updateAttributes({
      columns: configData.columns,
      rows: reconciledRows,
      likelihoodLabels: configData.likelihoodLabels,
      severityLabels: configData.severityLabels,
    });
    setIsConfigOpen(false);
  };

  const handleConfigCancel = (): void => {
    setIsConfigOpen(false);
  };

  return (
    <NodeViewWrapper contentEditable={false}>
      {/* Only show the selection outline while editing. In a read-only viewer the node
          must not look selectable, so we suppress it even if ProseMirror still tracks a
          NodeSelection under the click. */}
      <StyledRiskAssessmentContainer $selected={isEditable && selected}>
        <StyledRiskAssessmentBody>
          <RiskAssessmentTable
            columns={columns}
            rows={rows}
            likelihoodLabels={likelihoodLabels}
            severityLabels={severityLabels}
            editable={isEditable}
            onChange={handleRowChange}
            onDelete={handleRowDelete}
            onColumnResize={handleColumnResize}
          />

          {isEditable && (
            <StyledFooter>
              <StyledFooterActions>
                <Button variant="outlined" size="small" onClick={handleAddRow}>
                  + Add row
                </Button>
              </StyledFooterActions>
              <Tooltip title="Settings">
                <IconButton
                  size="small"
                  onClick={handleConfigOpen}
                  aria-label="Risk assessment settings"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </StyledFooter>
          )}
        </StyledRiskAssessmentBody>
      </StyledRiskAssessmentContainer>

      <RiskAssessmentConfigDialog
        open={isConfigOpen}
        configData={configData}
        onConfigDataChange={setConfigData}
        onSave={handleConfigSave}
        onCancel={handleConfigCancel}
      />
    </NodeViewWrapper>
  );
};
